import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import QuickAdd from '../components/todos/QuickAdd';
import TaskRow from '../components/todos/TaskRow';
import TaskDrawer from '../components/todos/TaskDrawer';
import TodoForm from '../components/todos/TodoForm';
import { useAuth } from '../context/AuthContext';
import { useConfirm } from '../components/ConfirmDialog';
import { todayISO } from '../lib/dates';
import {
  addTodo,
  appendActivity,
  addDaysISO,
  deleteTodo,
  groupTodosByDue,
  listProfiles,
  listTodos,
  updateTodo,
} from '../lib/todos';
import { LINK_TYPES } from '../lib/todoLinks';
import './Todo.css';

const DUE_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'Week' },
];

const SEARCH_THRESHOLD = 15;

export default function Todo() {
  const { user } = useAuth();
  const { taskId } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const today = todayISO();

  const [todos, setTodos] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [linkLabels, setLinkLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [status, setStatus] = useState('Open');
  const [assignee, setAssignee] = useState('all');
  const [dueFilter, setDueFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [collapsed, setCollapsed] = useState({ someday: true });
  const [openTask, setOpenTask] = useState(null);
  const [fullForm, setFullForm] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [rows, people] = await Promise.all([listTodos(), listProfiles()]);
      setTodos(rows);
      setProfiles(people);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /* Arriving from a calendar event's link: open that task.
   *
   * The id stays in the address until the drawer is closed. Navigating away
   * immediately looked tidier but silently broke this: /todo and /todo/:id
   * are different routes, so React remounts the page and the freshly-opened
   * drawer was thrown away with the rest of the state. */
  useEffect(() => {
    if (!taskId || !todos.length) return;
    const match = todos.find((t) => t.id === taskId);
    if (match) setOpenTask(match);
  }, [taskId, todos]);

  const closeDrawer = () => {
    setOpenTask(null);
    if (taskId) navigate('/todo', { replace: true });
  };

  /* Labels for linked records.
   *
   * Only the types actually used by a task are fetched — at most one query
   * per type, rather than loading every invoice and product to render a few
   * chips. Failure is contained: a chip without a label just doesn't render.
   */
  useEffect(() => {
    const types = [...new Set(todos.map((t) => t.link_type).filter(Boolean))];
    if (!types.length) return;
    let cancelled = false;
    Promise.all(
      types.map(async (type) => {
        try {
          const rows = await LINK_TYPES[type].list();
          return rows.map((r) => [`${type}:${r.id}`, r.label]);
        } catch {
          return [];
        }
      })
    ).then((pairs) => {
      if (!cancelled) setLinkLabels(Object.fromEntries(pairs.flat()));
    });
    return () => {
      cancelled = true;
    };
  }, [todos]);

  const withLabels = useMemo(
    () =>
      todos.map((t) => ({
        ...t,
        link_label: t.link_type ? linkLabels[`${t.link_type}:${t.link_id}`] || null : null,
      })),
    [todos, linkLabels]
  );

  const profileFor = (id) => profiles.find((p) => p.id === id) || null;

  // Status and assignee first: the counts on the due filters should reflect
  // whose list you're looking at, not the whole table.
  const scoped = withLabels.filter((t) => {
    if (status !== 'All' && t.status !== status) return false;
    if (assignee !== 'all' && t.assigned_to !== assignee) return false;
    return true;
  });

  const filtered = scoped.filter((t) => {
    if (dueFilter === 'overdue' && !(t.status !== 'Done' && t.due_date && t.due_date < today))
      return false;
    if (dueFilter === 'today' && t.due_date !== today) return false;
    if (dueFilter === 'week') {
      if (!t.due_date) return false;
      if (t.due_date > addDaysISO(today, 7)) return false;
    }
    const needle = query.trim().toLowerCase();
    if (needle && !`${t.title} ${t.notes || ''}`.toLowerCase().includes(needle)) return false;
    return true;
  });

  const sections = groupTodosByDue(filtered, today);

  const save = async (task, updates) => {
    // A due date that moves after the event was added leaves a stale event
    // in Google that this app cannot reach. Clearing the stamp and logging
    // it is what turns the button into "re-add" rather than lying.
    const dateMoved =
      (updates.due_date || null) !== (task.due_date || null) ||
      (updates.due_time || null) !== (task.due_time || null);
    const patch = { ...updates };
    let activity = task.activity;
    if (dateMoved && task.due_date) {
      activity = appendActivity({ activity }, 'rescheduled', `to ${updates.due_date || 'no date'}`);
    }
    if (dateMoved && task.added_to_calendar_at) {
      patch.added_to_calendar_at = null;
      activity = appendActivity({ activity }, 'calendar_stale');
    }
    patch.activity = activity;
    await updateTodo(task.id, patch);
    await load();
  };

  const toggle = async (task) => {
    const done = task.status !== 'Done';
    await updateTodo(task.id, {
      status: done ? 'Done' : 'Open',
      completed_at: done ? new Date().toISOString() : null,
      activity: appendActivity(task, done ? 'completed' : 'reopened'),
    });
    await load();
  };

  const snooze = async (task, days, label) => {
    const base = task.due_date && task.due_date > today ? task.due_date : today;
    const next = addDaysISO(base, days);
    await save(task, { due_date: next, due_time: task.due_time || null });
    await updateTodo(task.id, {
      activity: appendActivity({ activity: task.activity }, 'snoozed', `to ${label.toLowerCase()}`),
    });
    await load();
  };

  // Parking a task clears its date: "someday" and "due Tuesday" can't both
  // be true, and leaving the date behind would drop it back into a bucket
  // the moment it passed.
  const setSomeday = async (task) => {
    const parking = !task.is_someday;
    await updateTodo(task.id, {
      is_someday: parking,
      due_date: parking ? null : task.due_date,
      activity: appendActivity(task, parking ? 'parked' : 'unparked'),
    });
    await load();
  };

  const duplicate = async (task) => {
    await addTodo({
      title: task.title,
      notes: task.notes,
      due_date: task.due_date,
      due_time: task.due_time,
      priority: task.priority,
      assigned_to: task.assigned_to,
      link_type: task.link_type,
      link_id: task.link_id,
      is_someday: task.is_someday,
      created_by: user?.id,
      activity: [{ at: new Date().toISOString(), kind: 'created', detail: 'Duplicated' }],
    });
    await load();
  };

  const remove = async (task) => {
    if (
      !(await confirm({
        title: `Delete "${task.title}"?`,
        body: 'The task and its comments go with it. This cannot be undone.',
      }))
    )
      return;
    await deleteTodo(task.id);
    setOpenTask(null);
    await load();
  };

  const stampCalendar = async (task) => {
    await updateTodo(task.id, {
      added_to_calendar_at: new Date().toISOString(),
      activity: appendActivity(task, 'calendar_added'),
    });
    await load();
  };

  const quickAdd = async (fields) => {
    await addTodo({
      ...fields,
      priority: 'Normal',
      created_by: user?.id,
      activity: [{ at: new Date().toISOString(), kind: 'created', detail: null }],
    });
    await load();
  };

  const openCount = scoped.filter((t) => t.status !== 'Done').length;

  return (
    <>
      <PageHeader
        title="To-Do"
        subtitle="What needs doing, and when."
        actions={
          <button className="ghost-button" onClick={() => setFullForm((v) => !v)}>
            {fullForm ? 'Close' : '+ Add task'}
          </button>
        }
      />

      <div className="todo-toolbar">
        <div className="todo-segment">
          {DUE_FILTERS.map((f) => (
            <button
              key={f.key}
              className={'todo-segment-button' + (dueFilter === f.key ? ' active' : '')}
              onClick={() => setDueFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>

        <select className="lab-select" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="Open">Open</option>
          <option value="Done">Done</option>
          <option value="All">All</option>
        </select>

        <select className="lab-select" value={assignee} onChange={(e) => setAssignee(e.target.value)}>
          <option value="all">Everyone</option>
          {profiles.map((p) => (
            <option key={p.id} value={p.id}>
              {p.display_name || p.email}
            </option>
          ))}
        </select>

        {/* Search only earns its place once the list is long enough to need
            it — below that it's another control to look past. */}
        {todos.length > SEARCH_THRESHOLD && (
          <input
            className="lab-search"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search tasks"
          />
        )}
      </div>

      {fullForm && (
        /* No .panel wrapper: surfaces.css already makes .todo-form a raised
           card, and nesting one card in another gave two borders fighting,
           with the inner one overflowing the outer. */
        <div className="todo-full-form">
          <TodoForm
            profiles={profiles}
            onSubmit={async (fields) => {
              await addTodo({
                ...fields,
                created_by: user?.id,
                activity: [{ at: new Date().toISOString(), kind: 'created', detail: null }],
              });
              setFullForm(false);
              await load();
            }}
            onCancel={() => setFullForm(false)}
          />
        </div>
      )}

      {error && <p className="formula-error">Couldn't load your tasks: {error}</p>}

      {/* One card holding every row, sized to its content — not one card per
          task floating in an empty viewport. */}
      <section className="panel todo-card">
        <QuickAdd profiles={profiles} defaultAssignee={user?.id} onAdd={quickAdd} />

        {loading ? (
          <p className="formula-loading">Loading…</p>
        ) : sections.length === 0 ? (
          <div className="todo-empty">
            {openCount === 0 && dueFilter === 'all' && !query ? (
              <p>Nothing outstanding. Enjoy it while it lasts.</p>
            ) : (
              <>
                <p>No tasks match that filter.</p>
                <button
                  className="ghost-button"
                  onClick={() => {
                    setDueFilter('all');
                    setAssignee('all');
                    setStatus('Open');
                    setQuery('');
                  }}
                >
                  Clear filters
                </button>
              </>
            )}
          </div>
        ) : (
          sections.map((section) => {
            const isCollapsed = collapsed[section.key];
            return (
              <div className="todo-group" key={section.key}>
                <button
                  className={'todo-group-head ' + section.key}
                  onClick={() =>
                    setCollapsed((c) => ({ ...c, [section.key]: !c[section.key] }))
                  }
                >
                  <span>{section.label}</span>
                  <em>{section.todos.length}</em>
                </button>
                {!isCollapsed && (
                  <ul className="task-rows">
                    {section.todos.map((task) => (
                      <TaskRow
                        key={task.id}
                        task={task}
                        today={today}
                        profile={profileFor(task.assigned_to)}
                        onToggle={toggle}
                        onOpen={setOpenTask}
                        onSnooze={snooze}
                        onSomeday={setSomeday}
                        onDuplicate={duplicate}
                        onDelete={remove}
                        onCalendarAdded={stampCalendar}
                      />
                    ))}
                  </ul>
                )}
              </div>
            );
          })
        )}
      </section>

      {openTask && (
        <TaskDrawer
          task={withLabels.find((t) => t.id === openTask.id) || openTask}
          profiles={profiles}
          onClose={closeDrawer}
          onSave={save}
          onDelete={remove}
          onCalendarAdded={stampCalendar}
        />
      )}
    </>
  );
}
